import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Key, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import apiClient from "../../services/apiClient";
import { CreateUserModal, EditUserModal, ResetPasswordModal } from "./UserModals";

// Delete Confirmation Modal
const DeleteConfirmModal = ({ user, onClose, onConfirm }) => {
  const [confirmEmail, setConfirmEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (confirmEmail !== user.email) {
      toast.error('E-mail sa nezhoduje');
      return;
    }
    setLoading(true);
    await onConfirm();
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card p-6 w-full max-w-md mx-4">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-red-100 rounded-full">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-charcoal-800">Vymazať používateľa</h3>
        </div>
        
        <p className="text-charcoal-600 mb-4">
          Naozaj chcete vymazať používateľa <strong>{user.email}</strong>? 
          Táto akcia je nevratná.
        </p>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-red-800 mb-2">
            Pre potvrdenie zadajte e-mailovú adresu používateľa:
          </p>
          <input
            type="email"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            className="input-field w-full"
            placeholder={user.email}
          />
        </div>

        <div className="flex space-x-3">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose} 
            className="flex-1"
            disabled={loading}
          >
            Zrušiť
          </Button>
          <Button 
            type="button" 
            onClick={handleConfirm}
            disabled={confirmEmail !== user.email || loading}
            className="flex-1 bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
          >
            {loading ? 'Mazanie...' : 'Vymazať'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await apiClient.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Nepodarilo sa načítať používateľov');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await apiClient.delete(`/admin/users/${userId}`);
      toast.success('Používateľ bol úspešne vymazaný');
      setDeletingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('Nepodarilo sa vymazať používateľa');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="spinner"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-charcoal-800 font-display">User Management</h2>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-sage-500 hover:bg-sage-600 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      <div className="card p-4 sm:p-6">
        <div className="overflow-x-auto table-container">
          <table className="w-full">
            <thead>
              <tr className="border-b border-sage-200">
                <th className="text-left py-3 text-charcoal-700 font-medium">Email</th>
                <th className="text-left py-3 text-charcoal-700 font-medium hidden sm:table-cell">Role</th>
                <th className="text-left py-3 text-charcoal-700 font-medium hidden md:table-cell">Status</th>
                <th className="text-left py-3 text-charcoal-700 font-medium hidden lg:table-cell">Created</th>
                <th className="text-left py-3 text-charcoal-700 font-medium hidden lg:table-cell">End Date</th>
                <th className="text-left py-3 text-charcoal-700 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isExpired = user.end_date && new Date(user.end_date) < new Date();
                return (
                <tr key={user.id} className={`border-b border-cream-200 ${isExpired ? 'bg-red-50' : ''}`}>
                  <td className="py-3 text-charcoal-800">
                    <div>
                      <div className="font-medium">{user.email}</div>
                      <div className="sm:hidden text-xs text-charcoal-500 mt-1">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mr-2 ${
                          user.role === 'admin' ? 'bg-blue-100 text-blue-800' : 'bg-sage-100 text-sage-800'
                        }`}>{user.role}</span>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          isExpired ? 'bg-red-100 text-red-800' : user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>{isExpired ? 'Abgelaufen' : (user.is_active ? 'Active' : 'Inactive')}</span>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 hidden sm:table-cell">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.role === 'admin' ? 'bg-blue-100 text-blue-800' : 'bg-sage-100 text-sage-800'
                    }`}>{user.role}</span>
                  </td>
                  <td className="py-3 hidden md:table-cell">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      isExpired ? 'bg-red-100 text-red-800' : user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>{isExpired ? 'Abgelaufen' : (user.is_active ? 'Active' : 'Inactive')}</span>
                  </td>
                  <td className="py-3 text-charcoal-600 hidden lg:table-cell">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-3 text-charcoal-600 hidden lg:table-cell">
                    {user.role !== 'admin' ? (
                      user.end_date ? (
                        <span className={isExpired ? 'text-red-600 font-medium' : ''}>
                          {new Date(user.end_date).toLocaleDateString()}
                        </span>
                      ) : <span className="text-sage-600">Unbegrenzt</span>
                    ) : <span className="text-charcoal-400">-</span>}
                  </td>
                  <td className="py-3">
                    <div className="flex space-x-1">
                      <Button size="sm" variant="outline" onClick={() => setEditingUser(user)}
                        className="text-sage-600 border-sage-200 hover:bg-sage-50 p-2" title="Upraviť">
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setResettingPassword(user)}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50 p-2" title="Resetovať heslo">
                        <Key className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setDeletingUser(user)}
                        className="text-red-600 border-red-200 hover:bg-red-50 p-2" title="Vymazať">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showCreateModal && (
        <CreateUserModal 
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { setShowCreateModal(false); fetchUsers(); }}
        />
      )}

      {editingUser && (
        <EditUserModal 
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => { setEditingUser(null); fetchUsers(); }}
        />
      )}

      {resettingPassword && (
        <ResetPasswordModal 
          user={resettingPassword}
          onClose={() => setResettingPassword(null)}
          onSuccess={() => { setResettingPassword(null); fetchUsers(); }}
        />
      )}

      {deletingUser && (
        <DeleteConfirmModal 
          user={deletingUser}
          onClose={() => setDeletingUser(null)}
          onConfirm={() => handleDeleteUser(deletingUser.id)}
        />
      )}
    </div>
  );
};
